/**
 * Thông báo in-app + email (plan 2026-10-03 §1) — `/api/notifications/*`,
 * thông báo chung `/api/admin/announcements/`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";

export type NotificationCategory =
  "deadline" | "grade" | "forum" | "announcement" | "registration" | "appeal" | "system";

export interface AppNotification {
  id: number;
  category: NotificationCategory;
  category_label: string;
  title: string;
  body: string;
  link_url: string;
  created_at: string;
  read_at: string | null;
}

export interface NotificationListResponse {
  count: number;
  unread: number;
  results: AppNotification[];
}

export interface NotificationPreferences {
  timezone: string;
  quiet_start: string;
  quiet_end: string;
  email_disabled_categories: NotificationCategory[];
  categories: { value: NotificationCategory; label: string }[];
}

const KEY = ["notifications"] as const;

export function useUnreadCount(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "unread-count"],
    queryFn: async () =>
      (await apiClient.get<{ count: number }>("/notifications/unread-count/")).data.count,
    enabled,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useNotifications(
  params: { unread?: boolean; limit?: number; offset?: number },
  enabled = true,
) {
  return useQuery({
    queryKey: [...KEY, "list", params],
    queryFn: async () =>
      (
        await apiClient.get<NotificationListResponse>("/notifications/", {
          params: {
            unread: params.unread ? 1 : undefined,
            limit: params.limit,
            offset: params.offset,
          },
        })
      ).data,
    enabled,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => apiClient.post(`/notifications/${id}/read/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => apiClient.post("/notifications/read-all/"),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: [...KEY, "preferences"],
    queryFn: async () =>
      (await apiClient.get<NotificationPreferences>("/notifications/preferences/")).data,
  });
}

export function useSaveNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<NotificationPreferences>) =>
      (await apiClient.put<NotificationPreferences>("/notifications/preferences/", payload)).data,
    onSuccess: (data) => qc.setQueryData([...KEY, "preferences"], data),
  });
}

// ─── Thông báo chung (admin) ─────────────────────────────────────────────────

export type AnnouncementAudience = "tenant" | "major" | "course";

export interface Announcement {
  id: number;
  title: string;
  body: string;
  audience: AnnouncementAudience;
  audience_label: string;
  major: number | null;
  major_name: string;
  course: number | null;
  course_name: string;
  send_email: boolean;
  recipient_count: number;
  created_by: string;
  created_at: string;
}

export interface AnnouncementPayload {
  title: string;
  body: string;
  audience: AnnouncementAudience;
  major?: number;
  course?: number;
  send_email: boolean;
}

export function useAnnouncements() {
  return useQuery({
    queryKey: ["admin", "announcements"],
    queryFn: async () => (await apiClient.get<Announcement[]>("/admin/announcements/")).data,
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AnnouncementPayload) =>
      (await apiClient.post<Announcement>("/admin/announcements/", payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "announcements"] }),
  });
}
